import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { SubjectIcon, subjectGradient } from "@/components/SubjectIcon";
import LOCard from "@/components/LOCard";

export default function ClassDetail() {
    const { classNum } = useParams();
    const cn = parseInt(classNum, 10);
    const { t, subjectLabel, lang } = useApp();
    const [subjects, setSubjects] = useState([]);
    const [activeSubject, setActiveSubject] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.classSubjects(cn).then((s) => {
            setSubjects(s);
            if (s.length > 0) setActiveSubject(s[0].subject);
        });
    }, [cn]);

    useEffect(() => {
        if (!activeSubject) return;
        setLoading(true);
        api.listLO({ class_num: cn, subject: activeSubject, limit: 500 }).then((r) => {
            setItems(r.items || []);
            setLoading(false);
        });
    }, [cn, activeSubject]);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:pb-32">
            <Link to="/classes" data-testid="back-to-classes" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
                <ArrowLeft className="h-4 w-4" />
                {t.classes}
            </Link>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">{t.classes}</p>
                <h1 className={`font-display text-3xl sm:text-4xl font-extrabold tracking-tight ${lang === "od" ? "font-odia" : ""}`}>{t.classN(cn)}</h1>
            </motion.div>

            {/* Subject tabs */}
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-6">
                {subjects.map((s) => {
                    const active = activeSubject === s.subject;
                    return (
                        <button
                            key={s.subject}
                            onClick={() => setActiveSubject(s.subject)}
                            data-testid={`subject-tab-${s.subject.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold active:scale-95 transition-all ${
                                active ? `bg-gradient-to-r ${subjectGradient(s.subject)} text-white shadow-md` : "bg-muted hover:bg-muted/70"
                            }`}
                        >
                            <SubjectIcon subject={s.subject} className="h-4 w-4" />
                            <span className={lang === "od" ? "font-odia" : ""}>{subjectLabel(s.subject)}</span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${active ? "bg-white/25" : "bg-background"}`}>{s.count}</span>
                        </button>
                    );
                })}
            </div>

            {loading ? (
                <div className="text-center text-muted-foreground py-12">{t.loading}</div>
            ) : items.length === 0 ? (
                <div className="text-center text-muted-foreground py-12">{t.noResults}</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((entry, i) => (
                        <LOCard key={entry.id || entry.code} entry={entry} index={i} />
                    ))}
                </div>
            )}
        </div>
    );
}
